import { cleanup, findImageBySrcOrAlt, findNextTextNode, findPreviousTextNode, findTextRanges, getElementAndTextNode, htmlToText, isHighlightImageOnly, mapSameUrlSnippetsToHighlights, } from "./utils";
import { colors } from "../config/colors";
import {} from "$/types";
import { HighlightStore } from "$/stores/HighlightStore";
import { KnowledgeTriggerStore } from "$/stores/KnowledgeTriggerStore";
import { get } from "svelte/store";
import { AppStore, AppType } from "$/stores/AppStore";
import { ActiveTopic } from "$/stores/extension/ActiveTopicStore";
export const cssprefix = `cw-`;
/**
 * Visualizes highlights on the document.
 *
 * @param {Snippet[]} snippets - An array of snippet objects to visualize, can be only one [highlight] or multiple [highlights].
 * @param {string} url - The URL of the document to visualize the highlights on.
 * @returns {{ foundHighlights: DocumentHighlight[], notFoundHighlights: DocumentHighlight[] }} An object containing arrays of found and not found highlights.
 */
export function visualizeSnippets(snippets, url) {
    let foundHighlights = [];
    let notFoundHighlights = [];
    // @ts-ignore CSS.highlights.size
    if ((!(snippets === null || snippets === void 0 ? void 0 : snippets.length) && !CSS.highlights.size) || (snippets === null || snippets === void 0 ? void 0 : snippets.length) === 0) {
        console.log("visualizeSnippets early return - no snippets");
        return { foundHighlights, notFoundHighlights };
    }
    const currentUrl = url || window.location.href;
    let highlightsToVisualize = mapSameUrlSnippetsToHighlights(snippets, currentUrl);
    const cssRules = [];
    if (highlightsToVisualize.length > 0) {
        const highlightWithTextContent = highlightsToVisualize.map(({ content }) => htmlToText(content));
        const texts = highlightWithTextContent.filter((text) => text.trim().length > 0);
        const textsRanges = findTextRanges(texts);
        highlightsToVisualize.forEach((highlight, i) => {
            var _a;
            const currentOrAllOrTrigger = highlight.group === "trigger"
                ? "trigger"
                : highlightsToVisualize.length > 1 ? "all" : "current";
            if (!((_a = textsRanges[i]) === null || _a === void 0 ? void 0 : _a.length) && !isHighlightImageOnly(highlight)) {
                notFoundHighlights.push(highlight);
                return;
            }
            // Clear all existing CSS custom highlights
            //  cleanup(cssprefix + currentOrAllOrTrigger);
            const { id, highlight: { anchorNode, anchorOffset, focusNode, focusOffset }, url, originalId } = highlight;
            // if anchorNode or focusNode is not found, skip this highlight because it is likely social media content
            if (!anchorNode || !focusNode) {
                console.error(`Anchor or focus node from query not found with locator for highlight with id: ${id}`);
                notFoundHighlights.push(highlight);
                return;
            }
            const anchor = getElementAndTextNode(anchorNode, anchorOffset);
            const focus = getElementAndTextNode(focusNode, focusOffset);
            if (!(anchor === null || anchor === void 0 ? void 0 : anchor.node) || !(focus === null || focus === void 0 ? void 0 : focus.node)) {
                console.error(`Anchor or focus node not found with locator for highlight with id: ${id}`);
                notFoundHighlights.push(highlight);
                return;
            }
            let anchorElement = anchor.node;
            let anchorElementOffset = anchor.offset;
            if (focus === null || focus.node === null) {
                console.error(`Focus node not found for highlight with id: ${id}`);
                return;
            }
            let focusElement = focus.node;
            let focusElementOffset = focus.offset;
            if (anchorElement &&
                focusElement &&
                anchorElement.nodeType === Node.TEXT_NODE &&
                focusElement.nodeType === Node.TEXT_NODE) {
                if (anchorElement.compareDocumentPosition(focusElement) &
                    Node.DOCUMENT_POSITION_PRECEDING ||
                    (anchorElement === focusElement && anchorElementOffset > focusElementOffset)) {
                    [anchorElement, focusElement] = [focusElement, anchorElement];
                    [anchorElementOffset, focusElementOffset] = [
                        focusElementOffset,
                        anchorElementOffset
                    ];
                }
                const range = document.createRange();
                range.setStart(anchorElement, anchorElementOffset);
                range.setEnd(focusElement, focusElementOffset);
                const pos = range.getBoundingClientRect();
                const top = pos.top + window.scrollY;
                const left = pos.left + window.scrollX;
                const width = pos.width;
                const height = pos.height;
                HighlightStore.setHighlight(id, top, left, width, height, cssprefix + currentOrAllOrTrigger, url, highlight.content, highlight.highlight, originalId);
                cssRules.push(generateCssFromRange(range, highlight.id, highlight.group));
                foundHighlights.push(highlight);
            }
            else {
                notFoundHighlights.push(highlight);
            }
        });
        notFoundHighlights.forEach((highlight) => {
            const currentOrAllOrTrigger = highlight.group === "trigger"
                ? "trigger"
                : highlightsToVisualize.length > 1 ? "all" : "current";
            cssRules.push(...findHighlightByTextContent(highlight, currentOrAllOrTrigger));
        });
    }
    const style = (() => {
        let elem = document.getElementById(cssprefix + (highlightsToVisualize.length > 1 ? "all" : "current"));
        if (!elem) {
            elem = document.createElement("style");
            elem.id = cssprefix + (highlightsToVisualize.length > 1 ? "all" : "current");
            document.head.appendChild(elem);
        }
        return elem;
    })();
    style.innerHTML = cssRules.join("\n");
    return { foundHighlights, notFoundHighlights };
}
function findHighlightByTextContent(highlight, highlightGroup) {
    const cssRules = [];
    if (isHighlightImageOnly(highlight)) {
        const imgElement = findImageBySrcOrAlt(highlight.content);
        if (!imgElement) {
            console.error(`Image not found for highlight with id: ${highlight.id}`);
        }
        if (imgElement) {
            const pos = imgElement.getBoundingClientRect();
            const top = pos.top + window.scrollY;
            const left = pos.left + window.scrollX;
            const width = pos.width;
            const height = pos.height;
            HighlightStore.setHighlight(highlight.id, top, left, width, height, cssprefix + highlightGroup, highlight.url, highlight.content, highlight.highlight, highlight.originalId);
            const range = document.createRange();
            const nextNode = findNextTextNode(imgElement);
            if (nextNode && nextNode.nodeType === Node.TEXT_NODE) {
                range.setStart(nextNode, 0);
                range.setEnd(nextNode, 0);
            }
            else {
                const prevNode = findPreviousTextNode(imgElement);
                if (prevNode && prevNode.nodeType === Node.TEXT_NODE) {
                    range.setStart(prevNode, prevNode.textContent.length);
                    range.setEnd(prevNode, prevNode.textContent.length);
                }
                else {
                    console.error(`Text node not found for highlight with id: ${highlight.id}`);
                }
            }
            console.info(`Highlight with id: ${highlight.id} found by image`);
            cssRules.push(generateCssFromRange(range, highlight.id, highlight.group));
        }
    }
    else {
        const text = htmlToText(highlight.content);
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        let attempts = 0;
        const maxAttempts = 2;
        // @ts-ignore window.find
        while (window.find(text, false, false, true, false, false, false)) {
            const selection = window.getSelection();
            if (selection.anchorNode.parentElement.closest(".js-collabwriting-app") ||
                selection.anchorNode === document.body) {
                if (++attempts >= maxAttempts)
                    break;
                continue;
            }
            else {
                const range = selection.getRangeAt(0);
                const pos = range.getBoundingClientRect();
                const top = pos.top + window.scrollY;
                const left = pos.left + window.scrollX;
                const width = pos.width;
                const height = pos.height;
                HighlightStore.setHighlight(highlight.id, top, left, width, height, cssprefix + highlightGroup, highlight.url, highlight.content, highlight.highlight, highlight.originalId);
                console.info(`Highlight with id: ${highlight.id} found by text`);
                cssRules.push(generateCssFromRange(range, highlight.id, highlight.group));
                break;
            }
        }
        window.scrollTo(scrollX, scrollY);
    }
    return cssRules;
}
function generateCssFromRange(range, highlightId, group) {
    let cssRule;
    const cssId = `${cssprefix}${highlightId}`;
    const selection = window.getSelection();
    const highlightRange = document.createRange();
    highlightRange.setStart(range.startContainer, range.startOffset);
    highlightRange.setEnd(range.endContainer, range.endOffset);
    // Add the highlight to the CSS highlights map
    // @ts-ignore CSS.highlights.size
    CSS.highlights.set(cssId, new Highlight(highlightRange));
    const pos = range.getBoundingClientRect();
    let specificStyles;
    switch (group) {
        case "trigger":
            specificStyles = `
                text-decoration: underline !important;
                text-decoration-style: dashed !important;
                text-decoration-thickness: 2px !important;
                text-decoration-color: #38BDF8 !important;
                position: relative !important;
                cursor: pointer !important;
            `;
            cssRule = `
                ::highlight(${cssId}) {
                    all: unset;
                    color: black;
                    pointer-events: auto !important;
                    user-select: none !important;
                    -webkit-user-select: none !important;
                    -moz-user-select: none !important;
                    -ms-user-select: none !important;
                    cursor: pointer !important;
                    ${specificStyles}
                }
            `;
            // Create tooltip element
            const tooltip = document.createElement('div');
            tooltip.style.cssText = `
                position: fixed;
                background: white;
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 12px;
                white-space: nowrap;
                z-index: 1000;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                border: 1px solid #e5e7eb;
                pointer-events: none;
                opacity: 0;
    
            `;
            tooltip.textContent = "Most Highlighted";
            tooltip.id = `tooltip-${cssId}`;
            document.body.appendChild(tooltip);
            // Add scroll listener to hide tooltip
            document.addEventListener('scroll', () => {
                tooltip.style.opacity = '0';
            }, { passive: true });
            // Add hover styles using CSS
            const hoverStyle = document.createElement('style');
            hoverStyle.textContent = `
                ::highlight(${cssId}) {
                    color: inherit;
                    background-color: transparent;
                }
            `;
            document.head.appendChild(hoverStyle);
            // Add mousemove listener to position and show tooltip
            document.addEventListener('mousemove', (e) => {
                var _a;
                const range = document.caretRangeFromPoint(e.clientX, e.clientY);
                if (!range)
                    return;
                // @ts-ignore - CSS.highlights is not yet in TypeScript
                const highlight = CSS.highlights.get(cssId);
                if (!highlight)
                    return;
                let isOverHighlight = false;
                highlight.forEach((highlightRange) => {
                    if (range.compareBoundaryPoints(Range.START_TO_START, highlightRange) >= 0 &&
                        range.compareBoundaryPoints(Range.END_TO_END, highlightRange) <= 0) {
                        isOverHighlight = true;
                        const rect = highlightRange.getBoundingClientRect();
                        tooltip.style.left = (rect.left + (rect.width / 2)) + 'px';
                        tooltip.style.top = (rect.top - 30) + 'px';
                        tooltip.style.transform = 'translateX(-50%)';
                        tooltip.style.opacity = '1';
                        // Update highlight style for hover
                        if (!document.querySelector(`style[data-hover="${cssId}"]`)) {
                            const hoverStyleElement = document.createElement('style');
                            hoverStyleElement.setAttribute('data-hover', cssId);
                            hoverStyleElement.textContent = `
                                ::highlight(${cssId}) {
                                    background-color: #e0f2fe !important;
                                }
                            `;
                            document.head.appendChild(hoverStyleElement);
                        }
                    }
                });
                if (!isOverHighlight) {
                    tooltip.style.opacity = '0';
                    (_a = document.querySelector(`style[data-hover="${cssId}"]`)) === null || _a === void 0 ? void 0 : _a.remove();
                }
            });
            // Clean up hover style on scroll
            document.addEventListener('scroll', () => {
                var _a;
                tooltip.style.opacity = '0';
                (_a = document.querySelector(`style[data-hover="${cssId}"]`)) === null || _a === void 0 ? void 0 : _a.remove();
            }, { passive: true });
            break;
        default:
            specificStyles = `
                background-color: ${colors.get("yellow")} !important;
            `;
            cssRule = `
                ::highlight(${cssId}) {
                    all: unset;
                    color: black;
                    pointer-events: none !important;
                    user-select: none !important;
                    -webkit-user-select: none !important;
                    -moz-user-select: none !important;
                    -ms-user-select: none !important;
                    cursor: default !important;
                    ${specificStyles}
                }
            `;
            break;
    }
    // clear the selection
    selection.removeAllRanges();
    range.detach();
    return cssRule;
}
export function getPositionFromHighlight(highlight) {
    try {
        const anchor = getElementAndTextNode(highlight.anchorNode, highlight.anchorOffset);
        const focus = getElementAndTextNode(highlight.focusNode, highlight.focusOffset);
        if (!(anchor === null || anchor === void 0 ? void 0 : anchor.node) || !(focus === null || focus === void 0 ? void 0 : focus.node)) {
            return null;
        }
        let anchorElement = anchor.node;
        let anchorElementOffset = anchor.offset;
        let focusElement = focus.node;
        let focusElementOffset = focus.offset;
        if (anchorElement &&
            focusElement &&
            anchorElement.nodeType === Node.TEXT_NODE &&
            focusElement.nodeType === Node.TEXT_NODE) {
            if (anchorElement.compareDocumentPosition(focusElement) &
                Node.DOCUMENT_POSITION_PRECEDING ||
                (anchorElement === focusElement && anchorElementOffset > focusElementOffset)) {
                [anchorElement, focusElement] = [focusElement, anchorElement];
                [anchorElementOffset, focusElementOffset] = [
                    focusElementOffset,
                    anchorElementOffset
                ];
            }
            const range = document.createRange();
            range.setStart(anchorElement, anchorElementOffset);
            range.setEnd(focusElement, focusElementOffset);
            const pos = range.getBoundingClientRect();
            return {
                top: pos.top + window.scrollY,
                left: pos.left + window.scrollX,
                width: pos.width,
                height: pos.height
            };
        }
    }
    catch (error) {
        console.error('Error calculating position:', error);
    }
    return null;
}
